import assert from 'assert';
import React, {useEffect, useMemo, useRef, useState} from 'react';

import {assertAndContinue} from 'Asserts';
import BenchlingConfig from 'BenchlingConfig';
import {makeReactContext} from 'lib/makeReactContext';
import {CellModel, createCellModelSelector} from 'notebook/tables/state/CellState';
import {createRowModelSelector, RowModel} from 'notebook/tables/state/RowState';
import {TableModel, VolatileTableState} from 'notebook/tables/state/TableState';
import {getTableStateKey, StructuredTableStore} from 'notebook/tables/state/TableStore';
import {useAppSelector} from 'redux-stores/reduceApp';
import {BenchlingState} from 'redux-stores/reduceApp';
import {getStore} from 'redux-stores/Store';

interface TableContextInterface {
  tableSelector: (state: BenchlingState) => TableModel;
  tableKey: string;
  tableStateRef: React.MutableRefObject<TableModel>;
}

interface RowContextInterface {
  rowIndex: number;
  rowStateSelector: (state: BenchlingState) => RowModel;
}

const TableContext = makeReactContext<TableContextInterface>(() => {
  assert(false, 'TableContext must be used through TableStateWrapper');
}, 'TableContext');

const TableRowContext = makeReactContext<RowContextInterface>(() => {
  assert(false, 'TableRowContext must be used within a RowStateWrapper');
}, 'StructuredTableContext');

const MAX_RENDERS_BEFORE_ASSERT = 3;

export const TableStateWrapper = function ({
  children,
  provenanceUuid,
  fileId,
  tableState,
}: {
  children: React.ReactNode;
  provenanceUuid: string;
  fileId: string;
  tableState: VolatileTableState;
}): JSX.Element {
  const {table, staleRows, errorRows, warningRows, computedTable} = tableState;
  const tableKey = getTableStateKey(fileId, provenanceUuid);
  const [initialized, setInitialized] = useState(false);
  const renderCountRef = useRef(0);

  // TODO(BNCH-113857): Remove this once redux is enabled
  // tableStateRef is a temporary performance improvement while we are transitioning operations form TableEditable to
  // the selectors.  We are using a ref to store the table state so that we can update it without waiting
  // for the next render because of the asynchronous nature of dispatching an action thorugh a useEffect.
  // Once we have finished transitioning costly operations from TableEditable, this should be switched back
  // to pulling data from redux.
  const tableStateRef = useRef(tableState);
  // To enable pulling data from redux, switch back to createTableStateSelector(tableKey);
  const tableSelector = useMemo(() => () => tableStateRef.current, [tableStateRef]);
  useMemo(() => {
    tableStateRef.current = {table, staleRows, errorRows, warningRows, computedTable};
  }, [table, staleRows, errorRows, warningRows, computedTable]);

  const contextValue = useMemo(
    () => ({tableSelector, tableKey, tableStateRef}),
    [tableSelector, tableKey, tableStateRef]
  );

  // We must bypass the selectTable selector to get the table from the store directly since we know it will
  // be undefined for the first render
  const tableFromStore = useAppSelector((state) => state.ui.structuredTableStore.tables[tableKey]);

  useEffect(() => {
    if (BenchlingConfig.ELN_TABLE_ENABLE_REDUX_STATE) {
      getStore().dispatch(
        StructuredTableStore.actions.setVolatileTableData({
          tableKey,
          volatileTableState: {
            table,
            staleRows: staleRows || [],
            errorRows: errorRows || [],
            warningRows: warningRows || [],
            computedTable,
          },
        })
      );
    }
    setInitialized(true);
  }, [tableKey, table, staleRows, errorRows, warningRows, computedTable, setInitialized]);

  // Clean up the table store when the component is unmounted
  useEffect(
    () => () => {
      if (BenchlingConfig.ELN_TABLE_ENABLE_REDUX_STATE) {
        getStore().dispatch(StructuredTableStore.actions.removeTable({tableKey}));
      }
    },
    [tableKey]
  );

  // The useEffect to setTableData is queued to happen after the first render, which means that there will be
  // no data in the store for the first render, or potentially for a few renders if other react components
  // have already queued renders that must happen before this.  We should not render the children while this
  // is happening.  However, we need to make sure that this loading state does not persist indefinitely if
  // somehow the store is not updated.   So setting an assert that afer the dispatch has happened the store
  // must contain the table or else it should error.
  if (initialized && !tableFromStore && BenchlingConfig.ELN_TABLE_ENABLE_REDUX_STATE) {
    renderCountRef.current += 1;
    if (renderCountRef.current > MAX_RENDERS_BEFORE_ASSERT) {
      assertAndContinue(
        !initialized || tableFromStore,
        `Table ${tableKey} was not found in state after initialization`
      );
    }
  }
  return (
    <TableContext.Provider value={contextValue}>
      {!tableFromStore && BenchlingConfig.ELN_TABLE_ENABLE_REDUX_STATE ? <>Loading...</> : children}
    </TableContext.Provider>
  );
};

export function RowStateWrapper({
  rowIndex,
  children,
}: {
  rowIndex: number;
  children: React.ReactNode;
}): JSX.Element {
  const {tableSelector} = TableContext.useContext();

  const rowStateSelector = useMemo(
    () => createRowModelSelector(tableSelector, rowIndex),
    [tableSelector, rowIndex]
  );

  const contextValue = useMemo(() => ({rowIndex, rowStateSelector}), [rowIndex, rowStateSelector]);

  return <TableRowContext.Provider value={contextValue}>{children}</TableRowContext.Provider>;
}

export function useTable(): TableModel;
export function useTable<T>(subSelector: (state: TableModel) => T): T | null;
export function useTable<T>(subSelector?: (state: TableModel) => T): T | null {
  // For now, we don't support this hook without redux state so bail out immediately
  if (!BenchlingConfig.ELN_TABLE_ENABLE_REDUX_STATE) {
    return null;
  }
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const {tableSelector} = TableContext.useContext();
  // eslint-disable-next-line react-hooks/rules-of-hooks
  return useAppSelector((state) => {
    const tableState = tableSelector(state);
    assert(tableState, 'TableState must be initialized before useTable is called');
    if (!subSelector) {
      return tableState as T;
    }
    return subSelector(tableState);
  });
}

export function useTableRow(): RowModel;
export function useTableRow<T>(subSelector: (state: RowModel) => T): T | null;
export function useTableRow<T>(subSelector?: (state: RowModel) => T): T | null {
  // For now, we don't support this hook without redux state so bail out immediately
  if (!BenchlingConfig.ELN_TABLE_ENABLE_REDUX_STATE) {
    return null;
  }
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const {rowStateSelector} = TableRowContext.useContext();
  // eslint-disable-next-line react-hooks/rules-of-hooks
  return useAppSelector((state) => {
    const rowState = rowStateSelector(state);
    assert(rowState, `RowState not found`);
    if (!subSelector) {
      return rowState as T;
    }
    return subSelector(rowState);
  });
}

export function useTableCell(columnIndex: number): CellModel | null;
export function useTableCell<T>(columnIndex: number, subSelector: (cellState: CellModel) => T): T | null;
export function useTableCell<T>(columnIndex: number, subSelector?: (cellState: CellModel) => T): T | null {
  // For now, we don't support this hook without redux state so bail out immediately
  if (!BenchlingConfig.ELN_TABLE_ENABLE_REDUX_STATE) {
    return null;
  }
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const {rowIndex} = TableRowContext.useContext();
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const {rowStateSelector} = TableRowContext.useContext();

  // eslint-disable-next-line react-hooks/rules-of-hooks
  const cellStateSelector = useMemo(
    () => createCellModelSelector(rowStateSelector, columnIndex),
    [rowStateSelector, columnIndex]
  );

  // eslint-disable-next-line react-hooks/rules-of-hooks
  return useAppSelector((state) => {
    const cellState = cellStateSelector(state);
    assert(cellState, `CellState not found for row ${rowIndex} and column ${columnIndex}`);
    if (!subSelector) {
      return cellState as T;
    }
    return subSelector(cellState);
  });
}
